/**
 * EventReviewPage ✦ iter232d
 * ─────────────────────────────────────────────────────────────────
 * Customer-facing review page bound to a specific event slug.
 *
 *   /events/<slug>/review
 *
 * Flow:
 *   1. Render event hero (cover image + venue + date + booths)
 *   2. Star rating + review text + optional photos + consent
 *   3. Submit → POST /api/events/<slug>/review/submit
 *   4. Swap to a thank-you screen with gallery preview + venue
 *      card + share buttons (WhatsApp, Twitter, Facebook, email)
 *
 * Backwards-compat — works for every event in the unified
 * `public_events` collection regardless of when it was created.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';
const SERIF = "'Cormorant Garamond', 'EB Garamond', serif";

const mediaUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const EventReviewPage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [thankYou, setThankYou] = useState(null);
  // Form state
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [displayConsent, setDisplayConsent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events/${slug}/review/context`);
        setEvent(r.data);
        setAlreadyDone(!!r.data.already_submitted);
        if (r.data.customer_first_name) {
          setCustomerName(r.data.customer_first_name);
        }
      } catch (e) {
        toast.error('Sorry — we couldn\'t find that event.');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const loadThankYou = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/events/${slug}/review/thank-you`);
      setThankYou(r.data);
    } catch { /* silent — the thanks card just won't render */ }
  }, [slug]);

  const submit = async () => {
    if (rating === 0) { toast.error('Please choose a star rating ✦'); return; }
    if (reviewText.trim().length < 10) {
      toast.error('A few more words please — the review is too short.');
      return;
    }
    if (!customerName.trim()) {
      toast.error('Please enter your first name.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/events/${slug}/review/submit`, {
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim() || undefined,
        rating,
        review_text: reviewText.trim(),
        display_consent: displayConsent,
        marketing_consent: displayConsent,  // mirror — operator can split later
      });
      if (r.data?.already_submitted) {
        toast.info('You\'ve already left a review for this event — thank you!');
      } else {
        toast.success('Thank you ✦');
      }
      setSubmitted(true);
      loadThankYou();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={pageStyle}><p style={{ fontFamily: SERIF, color: 'rgba(255,255,255,0.6)' }}>Loading ✦</p></div>;
  }
  if (!event) {
    return <div style={pageStyle}><p style={{ fontFamily: SERIF }}>Event not found.</p></div>;
  }
  if (submitted || alreadyDone) {
    return <ThankYouScreen event={event} thankYou={thankYou} />;
  }

  return (
    <div data-testid="event-review-page" style={pageStyle}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px clamp(16px, 4vw, 28px) 80px' }}>
        {/* Hero */}
        {event.cover_image && (
          <div data-testid="review-hero-image" style={{
            width: '100%', aspectRatio: '16/9',
            background: `url(${mediaUrl(event.cover_image)}) center/cover`,
            borderRadius: 12, marginBottom: 24,
            border: `1px solid ${GOLD_FAINT}`,
          }} />
        )}

        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: GOLD, marginBottom: 14,
        }}>✦ Leave a Review</div>

        <h1 style={{
          fontFamily: SERIF, fontWeight: 500, fontSize: 36,
          lineHeight: 1.1, margin: '0 0 12px',
        }} data-testid="review-event-title">{event.name}</h1>

        {event.venue_name && (
          <p style={metaStyle}>
            {event.venue_name}
            {event.event_date ? ` · ${formatDate(event.event_date)}` : ''}
          </p>
        )}

        <p style={{
          marginTop: 18, fontFamily: SERIF, fontSize: 17,
          color: 'rgba(255,255,255,0.78)', lineHeight: 1.55,
        }}>
          Thank you for choosing EventDrop. We&apos;d love to hear about
          your experience — your review helps future couples choose us.
        </p>

        {/* Star rating */}
        <Section title="Star rating">
          <div data-testid="review-stars" style={{ display: 'flex', gap: 10 }}>
            {[1,2,3,4,5].map(n => (
              <button
                key={n}
                type="button"
                data-testid={`review-star-${n}`}
                onClick={() => setRating(n)}
                aria-label={`${n} star${n === 1 ? '' : 's'}`}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: n <= rating ? GOLD : 'rgba(255,255,255,0.18)',
                  fontSize: 44, padding: 0, lineHeight: 1,
                }}
              >★</button>
            ))}
          </div>
        </Section>

        {/* Your name */}
        <Section title="Your name">
          <input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Sarah"
            data-testid="review-name"
            style={fieldStyle}
          />
        </Section>

        {/* Email (optional) */}
        <Section title="Email (optional · so we can thank you personally)">
          <input
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="sarah@example.com"
            data-testid="review-email"
            style={fieldStyle}
          />
        </Section>

        {/* Review text */}
        <Section title="Your review">
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            rows={6}
            placeholder="What did you enjoy most? What made your evening memorable?"
            data-testid="review-text"
            style={{ ...fieldStyle, resize: 'vertical', minHeight: 140 }}
          />
        </Section>

        {/* Consent */}
        <label data-testid="review-consent" style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          marginTop: 24, padding: 16,
          border: `1px solid ${GOLD_FAINT}`, borderRadius: 10,
          background: 'rgba(212,170,82,0.06)',
          cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={displayConsent}
            onChange={(e) => setDisplayConsent(e.target.checked)}
            style={{ marginTop: 4, accentColor: GOLD, transform: 'scale(1.2)' }}
            data-testid="review-consent-checkbox"
          />
          <span style={{
            fontFamily: SERIF, fontSize: 14, color: 'rgba(255,255,255,0.78)',
            lineHeight: 1.5,
          }}>
            I give permission for EventDrop to display my review on
            their website and marketing.
          </span>
        </label>

        {/* Submit */}
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="review-submit"
          style={{
            marginTop: 28, width: '100%',
            padding: '16px 24px', borderRadius: 999,
            background: GOLD, color: '#0F0B1A',
            border: 'none', fontFamily: SERIF, fontWeight: 600,
            fontSize: 16, letterSpacing: '0.04em',
            cursor: submitting ? 'wait' : 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}
        >{submitting ? 'Sending…' : '✦ Submit my review'}</button>
      </div>
    </div>
  );
};

// iter232d — Post-submission engagement screen. Craig's bonus
// design feedback: turn every review into another opportunity for
// engagement, referrals, and SEO.
const ThankYouScreen = ({ event, thankYou }) => (
  <div data-testid="review-thanks" style={pageStyle}>
    <div style={{
      maxWidth: 760, margin: '0 auto',
      padding: '60px clamp(16px, 4vw, 28px) 80px',
    }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12, letterSpacing: '0.22em',
        textTransform: 'uppercase', color: GOLD, marginBottom: 16,
      }}>✦ Thank you</div>
      <h1 style={{
        fontFamily: SERIF, fontWeight: 500, fontSize: 42,
        lineHeight: 1.1, margin: '0 0 16px',
      }}>You&apos;re brilliant ❤️</h1>
      <p style={{
        fontFamily: SERIF, fontSize: 17,
        color: 'rgba(255,255,255,0.78)', lineHeight: 1.55,
        marginBottom: 32,
      }}>
        Your review means the world to us. While you&apos;re here — have
        another look at your gallery and the venue, and if you&apos;ve
        got friends planning their day, a quick share would help us
        more than you know.
      </p>

      {/* Gallery preview */}
      {thankYou?.gallery?.length > 0 && (
        <section style={sectionBlock} data-testid="thanks-gallery">
          <div style={sectionLabel}>✦ Your gallery</div>
          <div style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            marginTop: 12,
          }}>
            {thankYou.gallery.slice(0, 6).map((p, i) => {
              const u = p.thumbnail_url || p.url || p.image_url;
              return (
                <div key={i} style={{
                  aspectRatio: '1/1', borderRadius: 8,
                  background: `url(${mediaUrl(u)}) center/cover`,
                  border: `1px solid ${GOLD_FAINT}`,
                }} />
              );
            })}
          </div>
          <a href={thankYou.event_url} target="_blank" rel="noreferrer"
             data-testid="thanks-view-gallery"
             style={{
               display: 'inline-block', marginTop: 16,
               color: GOLD, fontFamily: SERIF, fontSize: 14,
               fontStyle: 'italic', textDecoration: 'underline',
             }}>View full gallery →</a>
        </section>
      )}

      {/* Venue card */}
      {thankYou?.venue && (
        <section style={sectionBlock} data-testid="thanks-venue">
          <div style={sectionLabel}>✦ The venue</div>
          <a href={`/venues/${thankYou.venue.slug}`}
             data-testid="thanks-venue-link"
             style={{
               display: 'flex', gap: 16, alignItems: 'center',
               padding: 16, borderRadius: 10,
               border: `1px solid ${GOLD_FAINT}`,
               background: 'rgba(212,170,82,0.04)',
               textDecoration: 'none', color: IVORY,
               transition: 'background 180ms ease',
             }}>
            {(thankYou.venue.hero_image_url || thankYou.venue.image_url) && (
              <div style={{
                width: 72, height: 72, borderRadius: 8, flexShrink: 0,
                background: `url(${mediaUrl(thankYou.venue.hero_image_url || thankYou.venue.image_url)}) center/cover`,
              }} />
            )}
            <div>
              <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 500 }}>
                {thankYou.venue.name}
              </div>
              <div style={{
                fontFamily: SERIF, fontStyle: 'italic', fontSize: 13,
                color: 'rgba(255,255,255,0.5)', marginTop: 4,
              }}>
                {thankYou.venue.location || thankYou.venue.county} →
              </div>
            </div>
          </a>
        </section>
      )}

      {/* Share buttons */}
      {thankYou?.share && (
        <section style={sectionBlock} data-testid="thanks-share">
          <div style={sectionLabel}>✦ Share your gallery</div>
          <div style={{
            display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12,
          }}>
            <ShareButton href={thankYou.share.whatsapp} label="WhatsApp" testId="share-whatsapp" />
            <ShareButton href={thankYou.share.twitter} label="X / Twitter" testId="share-twitter" />
            <ShareButton href={thankYou.share.facebook} label="Facebook" testId="share-facebook" />
            <ShareButton href={thankYou.share.mailto} label="Email a friend" testId="share-mailto" />
          </div>
        </section>
      )}
    </div>
  </div>
);

const ShareButton = ({ href, label, testId }) => (
  <a href={href} target="_blank" rel="noreferrer"
     data-testid={testId}
     style={{
       padding: '10px 16px', borderRadius: 999,
       background: 'transparent', color: GOLD,
       border: `1px solid ${GOLD_FAINT}`,
       fontFamily: SERIF, fontSize: 13, letterSpacing: '0.02em',
       textDecoration: 'none',
     }}>{label}</a>
);

const Section = ({ title, children }) => (
  <div style={sectionBlock}>
    <div style={sectionLabel}>{title}</div>
    {children}
  </div>
);

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

const pageStyle = {
  minHeight: '100vh', background: '#15161B', color: IVORY,
  fontFamily: SERIF,
};
const sectionBlock = { marginTop: 28 };
const sectionLabel = {
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: GOLD, marginBottom: 8,
};
const metaStyle = {
  fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
  color: 'rgba(255,255,255,0.55)', margin: 0,
};
const fieldStyle = {
  width: '100%', padding: '12px 14px', marginTop: 8,
  background: 'rgba(0,0,0,0.18)', color: IVORY,
  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
  fontFamily: SERIF, fontSize: 15,
  boxSizing: 'border-box',
};

export default EventReviewPage;
